import { useMemo } from 'react';
import { useOfferServiceCogs } from '@/models/offers';
import { TSplitLine, TSplitPlanItem, useSplitPlan } from '@/models/split-plan';
import { deriveSplit, DraftInstallment, OrderedService } from './splitDerive';

export function itemsToDraft(
  items: TSplitPlanItem[],
  fallbackCurrency: string,
): DraftInstallment[] {
  return [...items]
    .sort((a, b) => a.seq - b.seq)
    .map((it) => ({
      key: `item-${it.id}`,
      seq: it.seq,
      currency: it.currency || fallbackCurrency,
      paymentMethod: it.paymentMethod || 'Bank Transfer',
      payerId: it.payerId,
      amount: it.amount != null ? Number(it.amount) : null,
      lines: it.coveredLines,
      note: it.note ?? null,
    }));
}

export function useSplitReviewData(offerId: number, cogsEnabled = true) {
  const { data: planData, isLoading } = useSplitPlan(offerId);

  const base: OrderedService[] = useMemo(
    () =>
      (planData?.selectedLines ?? []).map((l: TSplitLine) => ({
        lineRef: {
          offerItemId: l.offerItemId,
          blockUuid: l.blockUuid,
          serviceId: l.serviceId,
          lineDisc: l.lineDisc,
        },
        serviceId: l.serviceId,
        name: l.serviceName,
        saleValue: Number(l.priceAmount) || 0,
        cogs: 0,
        cogsEur: 0,
        currency: l.priceCurrency,
        cogsEstimated: false,
        blockTitle: l.blockTitle,
        cpName: l.cpName,
      })),
    [planData?.selectedLines],
  );

  const currency = base[0]?.currency || 'EUR';
  const accountId = planData?.accountId ?? 0;

  const cogsInput = useMemo(
    () =>
      base.map((s) => ({
        serviceId: s.serviceId,
        priceCurrency: s.currency,
        priceAmount: s.saleValue,
      })),
    [base],
  );
  const { data: cogs } = useOfferServiceCogs(cogsInput, cogsEnabled);

  const services: OrderedService[] = useMemo(() => {
    const byId = new Map((cogs || []).map((c) => [c.serviceId, c]));
    return base.map((s) => ({
      ...s,
      cogs: byId.get(s.serviceId)?.cogsAmount ?? 0,
      cogsEur: byId.get(s.serviceId)?.cogsEur ?? 0,
      cogsEstimated: byId.get(s.serviceId)?.cogsEstimated ?? false,
    }));
  }, [base, cogs]);

  const itemsDraft = useMemo(
    () => itemsToDraft(planData?.items ?? [], currency),
    [planData?.items, currency],
  );

  // The order's value comes from the server, which is also what the guard measures.
  // What the installments must add up to is the AGREED total - the discounted figure the
  // customer was quoted and will see on the invoice - not the order before discount. The
  // amounts themselves already arrive as agreed money.
  const orderNetMinor = planData?.orderNet?.agreedMinor ?? null;
  // The order is valued in the plan's base currency, which on a mixed-currency order is
  // not the currency of its first line. Labelling the figure with that line's currency
  // put EUR money on screen with USD beside it.
  const orderNetCurrency = planData?.orderNet?.baseCurrency ?? currency;
  const discountPercent = planData?.orderNet?.discountPercent ?? 0;

  const derived = useMemo(
    () => deriveSplit(itemsDraft, services, orderNetMinor),
    [itemsDraft, services, orderNetMinor],
  );

  return {
    planData,
    orderNetMinor,
    orderNetCurrency,
    discountPercent,
    isLoading,
    base,
    services,
    currency,
    accountId,
    itemsDraft,
    derived,
  };
}
